import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { MemberNickname, MEMBER_NICKNAME_COLUMNS, dbRowToMemberNickname } from '@/types/memberNickname';

const NICKNAMES_CACHE_PREFIX = 'clando-nicknames-cache-v1:';
const NICKNAMES_REFETCH_MIN_INTERVAL_MS = 15 * 60 * 1000;

function loadNicknamesFromCache(teamId: string): MemberNickname[] {
    try {
        const raw = localStorage.getItem(`${NICKNAMES_CACHE_PREFIX}${teamId}`);
        return raw ? JSON.parse(raw) : [];
    } catch { return []; }
}

function saveNicknamesToCache(teamId: string, nicknames: MemberNickname[]) {
    try { localStorage.setItem(`${NICKNAMES_CACHE_PREFIX}${teamId}`, JSON.stringify(nicknames)); } catch {}
}

export function useNicknames(teamId: string | null) {
    const [nicknames, setNicknames] = useState<MemberNickname[]>(() => teamId ? loadNicknamesFromCache(teamId) : []);
    const lastFetchRef = useRef<number>(0);

    const fetchNicknames = useCallback(async () => {
        if (!teamId) {
            setNicknames([]);
            return;
        }

        const { data, error } = await supabase
            .from('member_nicknames')
            .select(MEMBER_NICKNAME_COLUMNS)
            .eq('team_id', teamId);

        if (error) {
            console.error('Error fetching nicknames:', error);
            const cached = loadNicknamesFromCache(teamId);
            if (cached.length > 0) setNicknames(cached);
            return;
        }

        const mapped = data ? data.map(dbRowToMemberNickname) : [];
        setNicknames(mapped);
        saveNicknamesToCache(teamId, mapped);
        lastFetchRef.current = Date.now();
    }, [teamId]);

    useEffect(() => {
        fetchNicknames();

        if (!teamId || typeof document === 'undefined') return;

        // Realtime removed (nicknames change very rarely). Throttled visibility refetch.
        const onVisible = () => {
            if (document.visibilityState !== 'visible') return;
            if (Date.now() - lastFetchRef.current < NICKNAMES_REFETCH_MIN_INTERVAL_MS) return;
            fetchNicknames();
        };
        document.addEventListener('visibilitychange', onVisible);
        return () => {
            document.removeEventListener('visibilitychange', onVisible);
        };
    }, [fetchNicknames, teamId]);

    const setNickname = useCallback(async (viewerId: string, targetId: string, nickname: string) => {
        if (!teamId) return;

        if (!nickname.trim()) {
            // Revert to normal name by deleting
            const { error } = await supabase
                .from('member_nicknames')
                .delete()
                .match({ team_id: teamId, viewer_id: viewerId, target_id: targetId });

            if (error) {
                console.error('Error deleting nickname:', error);
                throw error;
            }
            return;
        }

        const { error } = await supabase
            .from('member_nicknames')
            .upsert({
                team_id: teamId,
                viewer_id: viewerId,
                target_id: targetId,
                nickname: nickname.trim(),
            }, { onConflict: 'viewer_id,target_id' });

        if (error) {
            console.error('Error setting nickname:', error);
            throw error;
        }
    }, [teamId]);

    return {
        nicknames,
        setNickname,
    };
}
